import { useState, useEffect, useCallback, useRef } from 'react';
import { API } from '@/App';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Microphone, MicrophoneSlash, Stop, Play, CheckCircle,
  XCircle, ArrowClockwise, Waveform, PaperPlaneTilt,
  Gear, MagnifyingGlass, Bug, CalendarBlank,
  Clock, CaretRight, Spinner, Warning
} from '@phosphor-icons/react';

const MODES = [
  { id: 'auto', label: 'Auto', icon: Waveform, desc: 'AI detects intent' },
  { id: 'setup', label: 'Setup', icon: Gear, desc: 'Configure APIs' },
  { id: 'query', label: 'Query', icon: MagnifyingGlass, desc: 'Ask questions' },
  { id: 'troubleshoot', label: 'Troubleshoot', icon: Bug, desc: 'Diagnose issues' },
  { id: 'schedule', label: 'Schedule', icon: CalendarBlank, desc: 'Manage jobs' },
];

const STATUS_STYLES = {
  pending_confirmation: { color: 'kova-amber', label: 'AWAITING CONFIRM' },
  executed: { color: 'kova-green', label: 'EXECUTED' },
  cancelled: { color: 'kova-slate', label: 'CANCELLED' },
};

export default function VoicePage() {
  const [mode, setMode] = useState('auto');
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [manualInput, setManualInput] = useState('');
  const [processing, setProcessing] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [history, setHistory] = useState([]);
  const [speechSupported, setSpeechSupported] = useState(true);
  const recognitionRef = useRef(null);
  const sessionIdRef = useRef(`vs_${Date.now().toString(36)}`);

  // Check Web Speech API support
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setSpeechSupported(false);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API}/voice/history?limit=20`, { credentials: 'include' });
      if (res.ok) setHistory(await res.json());
    } catch (e) { console.error('Voice history fetch error:', e); }
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  // ─── Speech Recognition ───
  const startListening = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error('Speech recognition not supported in this browser');
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onresult = (event) => {
      let interim = '';
      let final = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          final += event.results[i][0].transcript;
        } else {
          interim += event.results[i][0].transcript;
        }
      }
      setTranscript(prev => (final ? (prev ? prev + ' ' : '') + final : prev || interim));
    };

    recognition.onerror = (event) => {
      if (event.error !== 'no-speech') {
        toast.error(`Speech error: ${event.error}`);
      }
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
    setTranscript('');
    setCurrentPlan(null);
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsListening(false);
  };

  // ─── Process Command ───
  const processCommand = async (text) => {
    if (!text?.trim()) return;
    setProcessing(true);
    setCurrentPlan(null);
    try {
      const res = await fetch(`${API}/voice/process`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: text.trim(),
          mode: mode === 'auto' ? null : mode,
          session_id: sessionIdRef.current,
        }),
      });
      if (res.ok) {
        const plan = await res.json();
        setCurrentPlan(plan);
        fetchHistory();
      } else {
        const d = await res.json();
        toast.error(d.detail || 'Processing failed');
      }
    } catch { toast.error('Processing failed'); }
    setProcessing(false);
  };

  // ─── Execute Plan ───
  const executePlan = async () => {
    if (!currentPlan?.command_id) return;
    setExecuting(true);
    try {
      const res = await fetch(`${API}/voice/execute`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ command_id: currentPlan.command_id }),
      });
      if (res.ok) {
        const result = await res.json();
        toast.success('Plan executed successfully');
        setCurrentPlan({ ...currentPlan, executed: true, result });
        fetchHistory();
      } else {
        const d = await res.json();
        toast.error(d.detail || 'Execution failed');
      }
    } catch { toast.error('Execution failed'); }
    setExecuting(false);
  };

  // ─── Cancel Plan ───
  const cancelPlan = async () => {
    if (!currentPlan?.command_id) return;
    try {
      await fetch(`${API}/voice/cancel/${currentPlan.command_id}`, {
        method: 'POST', credentials: 'include',
      });
      setCurrentPlan(null);
      toast('Command cancelled');
      fetchHistory();
    } catch { /* ignore */ }
  };

  return (
    <div className="space-y-6" data-testid="voice-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-kova-white tracking-tight">Voice Control</h1>
          <p className="font-mono text-[10px] tracking-[2px] uppercase text-kova-slate mt-1">F-013 Natural Language Voice Interface</p>
        </div>
      </div>

      {/* Mode Selector */}
      <div className="flex gap-0 border border-kova-cyan/12 bg-kova-card">
        {MODES.map(m => (
          <button
            key={m.id}
            data-testid={`mode-${m.id}`}
            onClick={() => setMode(m.id)}
            className={`flex-1 flex flex-col items-center gap-1 py-3 px-2 font-mono text-[9px] tracking-[1.5px] uppercase transition-colors border-r border-kova-cyan/12 last:border-r-0 ${
              mode === m.id
                ? 'bg-kova-cyan/10 text-kova-cyan'
                : 'text-kova-slate hover:text-kova-silv hover:bg-kova-card2'
            }`}
          >
            <m.icon size={18} weight={mode === m.id ? 'fill' : 'regular'} />
            {m.label}
          </button>
        ))}
      </div>

      {/* Voice Input Area */}
      <div className="bg-kova-card border border-kova-cyan/12 p-6">
        <div className="flex flex-col items-center gap-4">
          {/* Microphone Button */}
          {speechSupported ? (
            <button
              data-testid="mic-button"
              onClick={isListening ? stopListening : startListening}
              disabled={processing}
              className={`relative w-20 h-20 flex items-center justify-center transition-all ${
                isListening
                  ? 'bg-kova-red text-white'
                  : 'bg-kova-cyan/10 text-kova-cyan hover:bg-kova-cyan/20 border border-kova-cyan/25'
              } disabled:opacity-50`}
            >
              {isListening && (
                <motion.div
                  className="absolute inset-0 border-2 border-kova-red/50"
                  animate={{ scale: [1, 1.3, 1], opacity: [0.5, 0, 0.5] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                />
              )}
              {isListening ? <Stop size={32} weight="fill" /> : <Microphone size={32} />}
            </button>
          ) : (
            <div className="flex items-center gap-2 text-kova-amber font-mono text-[10px]">
              <Warning size={14} />
              Speech recognition not supported — use text input below
            </div>
          )}

          <p className="font-mono text-[9px] tracking-[2px] uppercase text-kova-slate">
            {isListening ? 'Listening... Speak your command' : speechSupported ? 'Click to start voice input' : 'Type your command below'}
          </p>

          {/* Transcript */}
          {transcript && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="w-full bg-kova-bg border border-kova-cyan/12 p-3"
            >
              <p className="font-mono text-[9px] tracking-[2px] uppercase text-kova-slate mb-1">Transcript</p>
              <p className="text-kova-white text-sm" data-testid="transcript-text">{transcript}</p>
              <div className="flex justify-end mt-2 gap-2">
                <button
                  onClick={() => setTranscript('')}
                  className="font-mono text-[9px] tracking-[1px] text-kova-slate hover:text-kova-silv px-2 py-1"
                >
                  Clear
                </button>
                <button
                  data-testid="process-transcript-btn"
                  onClick={() => processCommand(transcript)}
                  disabled={processing}
                  className="flex items-center gap-1 font-mono text-[10px] tracking-[2px] uppercase bg-kova-cyan text-kova-bg px-3 py-1.5 hover:bg-kova-cyan2 disabled:opacity-50"
                >
                  {processing ? <ArrowClockwise size={12} className="animate-spin" /> : <PaperPlaneTilt size={12} />}
                  Process
                </button>
              </div>
            </motion.div>
          )}

          {/* Manual Text Input */}
          <div className="w-full flex gap-2">
            <input
              data-testid="manual-input"
              type="text"
              value={manualInput}
              onChange={e => setManualInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && manualInput.trim()) { processCommand(manualInput); setManualInput(''); } }}
              placeholder="Or type a command..."
              className="flex-1 bg-kova-bg border border-kova-cyan/12 text-kova-white font-mono text-xs px-3 py-2 placeholder:text-kova-slate focus:outline-none focus:border-kova-cyan"
            />
            <button
              data-testid="send-text-btn"
              onClick={() => { if (manualInput.trim()) { processCommand(manualInput); setManualInput(''); } }}
              disabled={processing || !manualInput.trim()}
              className="flex items-center gap-1 font-mono text-[10px] tracking-[2px] uppercase bg-kova-cyan text-kova-bg px-4 py-2 hover:bg-kova-cyan2 disabled:opacity-50 cursor-crosshair"
            >
              {processing ? <Spinner size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} />}
              Send
            </button>
          </div>
        </div>
      </div>

      {/* Plan Review (Plan-Confirm-Execute) */}
      <AnimatePresence>
        {currentPlan && !currentPlan.executed && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-kova-card border border-kova-cyan/12 p-5"
            data-testid="plan-review"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="font-mono text-[9px] tracking-[2px] uppercase text-kova-slate">Action Plan</span>
                <span className={`font-mono text-[8px] tracking-[1px] uppercase px-1.5 py-0.5 border ${
                  currentPlan.needs_clarification
                    ? 'border-kova-amber/20 bg-kova-amber/10 text-kova-amber'
                    : 'border-kova-cyan/20 bg-kova-cyan/10 text-kova-cyan'
                }`}>
                  {currentPlan.needs_clarification ? 'Needs Clarification' : currentPlan.mode?.toUpperCase()}
                </span>
              </div>
            </div>

            {/* Intent */}
            <p className="text-kova-white text-sm mb-3">{currentPlan.intent}</p>

            {/* Plan Steps */}
            {currentPlan.plan_steps?.length > 0 && (
              <div className="space-y-1 mb-4">
                {currentPlan.plan_steps.map((step, i) => (
                  <div key={i} className="flex items-start gap-2 text-kova-silv text-xs font-mono">
                    <CaretRight size={12} className="text-kova-cyan mt-0.5 flex-shrink-0" />
                    <span>{step}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2 border-t border-kova-cyan/12">
              <button
                data-testid="execute-plan-btn"
                onClick={executePlan}
                disabled={executing || currentPlan.needs_clarification}
                className="flex items-center gap-2 font-mono text-[10px] tracking-[2px] uppercase bg-kova-cyan text-kova-bg px-4 py-2 hover:bg-kova-cyan2 disabled:opacity-50 cursor-crosshair"
              >
                {executing ? <ArrowClockwise size={14} className="animate-spin" /> : <CheckCircle size={14} weight="bold" />}
                {executing ? 'Executing...' : 'Confirm & Execute'}
              </button>
              <button
                data-testid="cancel-plan-btn"
                onClick={cancelPlan}
                className="flex items-center gap-2 font-mono text-[10px] tracking-[2px] uppercase text-kova-silv border border-kova-cyan/12 px-4 py-2 hover:border-kova-cyan/25"
              >
                <XCircle size={14} /> Cancel
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Execution Result */}
      <AnimatePresence>
        {currentPlan?.executed && currentPlan.result && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-kova-card border border-kova-green/25 p-5"
            data-testid="execution-result"
          >
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle size={16} weight="fill" className="text-kova-green" />
              <span className="font-mono text-[9px] tracking-[2px] uppercase text-kova-green">Execution Complete</span>
            </div>
            {currentPlan.result.results?.map((r, i) => (
              <div key={i} className="bg-kova-bg border border-kova-cyan/12 p-3 mb-2 font-mono text-xs">
                <pre className="text-kova-silv whitespace-pre-wrap">{JSON.stringify(r, null, 2)}</pre>
              </div>
            ))}
            <button
              onClick={() => setCurrentPlan(null)}
              className="font-mono text-[9px] tracking-[1px] text-kova-slate hover:text-kova-silv mt-2"
            >
              Dismiss
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Command History */}
      <div className="bg-kova-card border border-kova-cyan/12">
        <div className="px-5 py-3 border-b border-kova-cyan/12">
          <span className="font-mono text-[9px] tracking-[2px] uppercase text-kova-slate">Command History</span>
        </div>
        {history.length === 0 ? (
          <div className="p-8 text-center text-kova-slate font-mono text-xs">No voice commands yet</div>
        ) : (
          <div>
            {history.map((cmd, i) => {
              const style = STATUS_STYLES[cmd.status] || STATUS_STYLES.pending_confirmation;
              return (
                <div key={cmd.command_id || i} className="px-5 py-2.5 border-b border-kova-cyan/6 flex items-center justify-between font-mono text-xs">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <Clock size={12} className="text-kova-slate flex-shrink-0" />
                    <span className="text-kova-white truncate" title={cmd.text}>{cmd.text}</span>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                    <span className="text-kova-slate text-[9px] uppercase">{cmd.mode}</span>
                    <span className={`text-${style.color} text-[8px] tracking-[1px]`}>{style.label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
